import React, { useEffect, useState } from "react";
import { Auction } from "../../../models/auction";
import Papa from "papaparse";
import {
  LegendComponent,
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import {
  FormatAuctionDaily,
  FormatAuctionDailyComparison,
  FormatAuctionMonthly,
  FormatAuctionMonthlyComparison,
} from "./FormatData";
import { COLOR_CODES, formatDMY, PrimaryColor } from "../../../common";
import { SearchBox } from "../../../components/Search";
import { Pagination } from "../../../components/Pagination";
import Loading from "../../../components/Loading";

import { FilterTab } from "../../../components/Filters";
import Dropdown from "../../../components/Dropdown";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import MainPageContainer from "../../../components/MainPageContainer";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface RAProps {
  forecastData: Auction[];
  startDate: Date;
  endDate: Date;
  loading: boolean;
}

export const RA: React.FC<RAProps> = ({
  forecastData,
  startDate,
  endDate,
  loading,
}) => {
  const active = useSelector((state: RootState) => state.menu.isActive);
  const [isByChart, setIsByChart] = useState<boolean>(true);
  const [isByMonthlyChart, setIsByMonthlyChart] = useState<boolean>(false);
  const [isComparative, setIsComparative] = useState<boolean>(true);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [selectExc, setSelectExc] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedAuctioNumber, setSelectedAuctioNumber] = useState<string[]>(
    []
  );
  // Sorting state
  const [sortField, setSortField] = useState<keyof Auction>("auction_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedAuctionData, setSelectedAuctionData] = useState<string[]>([]);
  const EXCHANGE_COLOR_MAP: { [key: string]: string } = {
    DEEP: ExchangeColors[1],
    IEX: COLOR_CODES.iex,
    HPX: COLOR_CODES.hpx,
    PXIL: COLOR_CODES.pixl,
  };

  const ITEMS_PER_PAGE = 100;

  const handleSelectType = (type: string) => {
    if (EXCHANGE_TYPES.length === selectExc.length) {
      setSelectExc([type]);
      return;
    }
    if (selectExc.includes(type) && selectExc.length === 1) {
      setSelectExc(EXCHANGE_TYPES);
      return;
    }
    setSelectExc((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type]
    );
    setCurrentPage(1);
  };

  const handleSelectAuction = (auctionNumber: string) => {
    setSelectedAuctioNumber((prev) =>
      prev.includes(auctionNumber)
        ? prev.filter((item) => item !== auctionNumber)
        : [...prev, auctionNumber]
    );
  };

  const filteredAuctions = (
    filterByEntity = true,
    filterByAuctionNumber = true,
    filterByAuctionDate = true
  ): Auction[] => {
    let res = forecastData;
    if (selectExc.length > 0) {
      res = res.filter((auction) =>
        selectExc.includes(auction.exchange_type__name!)
      );
    }
    if (selectedEntity.length > 0 && filterByEntity) {
      res = res.filter((auction) =>
        selectedEntity.includes(auction.buyer__name!)
      );
    }
    if (selectedAuctioNumber.length > 0 && filterByAuctionNumber) {
      res = res.filter((auction) =>
        selectedAuctioNumber.includes(auction.auction_no!)
      );
    }
    if (selectedAuctionData.length > 0 && filterByAuctionDate) {
      res = res.filter((auction) =>
        selectedAuctionData.includes(auction.auction_date!)
      );
    }
    return res;
  };

  const getActiveExc = () => {
    return selectExc.length > 0 ? selectExc : EXCHANGE_TYPES;
  };

  // Get a comparable value for sorting; attempts to convert date/time fields to numbers
  const getComparableValue = (auction: Auction, field: keyof Auction) => {
    const value = auction[field];
    if (value === null || value === undefined) return "";
    if (
      field.toString().includes("date") ||
      field.toString().includes("time")
    ) {
      const parsed = Date.parse(value as string);
      return isNaN(parsed) ? value : parsed;
    }
    return value;
  };

  // Sort auctions based on the sort state
  const sortedAuctions = filteredAuctions().sort((a, b) => {
    const aVal = getComparableValue(a, sortField);
    const bVal = getComparableValue(b, sortField);

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    }
    const aStr = aVal ? aVal.toString() : "";
    const bStr = bVal ? bVal.toString() : "";
    return sortOrder === "asc"
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  interface AuctionTableProps {
    auctions: Auction[];
  }

  const AuctionTable = ({ auctions }: AuctionTableProps) => {
    // Sorting state
    const [sortField, setSortField] = useState<keyof Auction>("auction_date");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Reset currentPage whenever auctions prop changes
    useEffect(() => {
      setCurrentPage(1);
    }, [auctions]);

    // Toggle sort order or change sort field
    const handleSort = (field: keyof Auction) => {
      if (sortField === field) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortField(field);
        setSortOrder("asc");
      }
    };

    // Calculate paginated auctions
    const paginatedAuctions = sortedAuctions.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );

    // Style for table data cells
    const tdStyle = {
      padding: "8px 12px",
      textAlign: "center" as const, // Center the text
      borderBottom: "1px solid #d9d9d9", // Consistent border color
      whiteSpace: "nowrap" as const,
    };

    // Style for auction number cells
    const auctionNoStyle = {
      ...tdStyle,
      position: "relative" as const,
      zIndex: 1,
    };

    // Hover style for auction number cells
    const auctionNoHoverStyle = `
      .auction_no {
        transition: background-color 0.2s ease;
      }
      .auction_no:hover {
        z-index: 10;
      }
    `;

    // Column widths configuration
    const columnWidths = {
      auction_no: "300px",
      auction_date: "160px",
      delivery_start_date: "160px",
      delivery_start_time: "140px",
      delivery_end_date: "160px",
      delivery_end_time: "140px",
      accepted_price_kwh: "220px",
      allocated_quantity_mw: "220px",
      buy_total_quantity_mw: "200px",
      buyer__name: "300px",
      exchange_type__name: "220px",
    };

    return (
      <div className="detContainermain container-radius text-nowrap">
        {/* Add style tag for custom hover effect */}
        <style>{auctionNoHoverStyle}</style>

        <div
          className="mt-2"
          style={{ height: "calc(100vh - 340px)", overflow: "auto" }}
        >
          <table className="w-full table-fixed border-0">
            <colgroup>
              <col style={{ width: columnWidths.auction_no }} />
              <col style={{ width: columnWidths.auction_date }} />
              <col style={{ width: columnWidths.delivery_start_date }} />
              <col style={{ width: columnWidths.delivery_start_time }} />
              <col style={{ width: columnWidths.delivery_end_date }} />
              <col style={{ width: columnWidths.delivery_end_time }} />
              <col style={{ width: columnWidths.accepted_price_kwh }} />
              <col style={{ width: columnWidths.allocated_quantity_mw }} />
              <col style={{ width: columnWidths.buy_total_quantity_mw }} />
              <col style={{ width: columnWidths.buyer__name }} />
              <col style={{ width: columnWidths.exchange_type__name }} />
            </colgroup>
            <thead className="border-0 ">
              <tr className="bg-gray-50">
                <th
                  rowSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]"
                >
                  Auction No
                </th>
                <th
                  rowSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]"
                >
                  Auction Date
                </th>
                <th
                  colSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-b border-[#fdd3bb] text-[#34656D]"
                >
                  Delivery Starting
                </th>
                <th
                  colSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-b border-[#fdd3bb] text-[#34656D]"
                >
                  Delivery Ending
                </th>
                <th
                  rowSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]"
                >
                  Allocated Price (kWh)
                </th>
                <th
                  rowSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]"
                >
                  Allocated QTY (MW)
                </th>
                <th
                  rowSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]"
                >
                  Buy QTY (MW)
                </th>
                <th
                  rowSpan={2}
                  className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]"
                >
                  Buyer
                </th>
                <th rowSpan={2} className="py-2 px-6 text-center border-0">
                  Exchange
                </th>
              </tr>
              <tr className="bg-gray-50">
                <th className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]">
                  Date
                </th>
                <th className="py-2 px-6 text-center border-0 border-r  border-[#fdd3bb]">
                  Time
                </th>
                <th className="py-2 px-6 text-center border-0 border-r  border-[#fdd3bb]">
                  Date
                </th>
                <th className="py-2 px-6 text-center border-0 border-r border-[#fdd3bb]">
                  Time
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedAuctions.map((auction) => (
                <tr
                  className="hover:bg-gray-50 text-[#8C8A96] text-[12px] border-0 border-b border-[#DFEAF2]"
                  key={auction.id}
                >
                  <td style={auctionNoStyle} className="auction_no">
                    {auction.auction_no}
                  </td>
                  <td style={tdStyle}>
                    {auction.auction_date
                      ? formatDMY(auction.auction_date)
                      : ""}
                  </td>
                  <td style={tdStyle}>
                    {auction.delivery_start_date
                      ? formatDMY(auction.delivery_start_date)
                      : ""}
                  </td>
                  <td style={tdStyle}>{auction.delivery_start_time}</td>
                  <td style={tdStyle}>
                    {auction.delivery_end_date
                      ? formatDMY(auction.delivery_end_date)
                      : ""}
                  </td>
                  <td style={tdStyle}>
                    {auction.delivery_end_time === "23:59:59"
                      ? "24:00:00"
                      : auction.delivery_end_time}
                  </td>
                  <td style={{ ...tdStyle, color: COLOR_CODES.secondary }}>
                    {auction.accepted_price_kwh !== null
                      ? auction.accepted_price_kwh.toFixed(2)
                      : ""}
                  </td>
                  <td style={{ ...tdStyle, color: COLOR_CODES.primary }}>
                    {auction.allocated_quantity_mw}
                  </td>
                  <td style={tdStyle}>{auction.buy_total_quantity_mw}</td>
                  <td style={tdStyle} className="buyerList2">
                    {auction.buyer__name}
                  </td>
                  <td style={tdStyle}>{auction.exchange_type__name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Component */}
        <Pagination
          currentPage={currentPage}
          totalItems={sortedAuctions.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>
    );
  };

  function HandleDownload(data: any) {
    interface ModifiedAuction extends Omit<Auction, "id"> {}

    const ModifiedData: ModifiedAuction[] = data.map((auction: Auction) => {
      const { id, ...rest } = auction;
      return rest;
    });

    var csv = Papa.unparse(ModifiedData);
    var link = document.createElement("a");
    link.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv)
    );
    link.setAttribute("download", "Reverse_Auction_Data.csv");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <>
      <div
        style={{
          width: active ? "calc(100vw - 160px)" : "calc(100vw - 100px)",
        }}
        className="m-4"
      >
        {loading && <Loading />}

        <div className="filterSection border mb-4">
          <FilterTab
            options={["Chart", "Table"]}
            isRounded={true}
            selected={isByChart ? ["Chart"] : ["Table"]}
            isSecondary={false}
            onClick={(type: string) => setIsByChart(type === "Chart")}
          />
          <div className="flex space-x-4 w-full">
            <FilterTab
              options={EXCHANGE_TYPES}
              isRounded={true}
              selected={selectExc}
              isSecondary={true}
              onClick={(type: string) => handleSelectType(type)}
            />

            {isByChart && (
              <Dropdown
                options={["Cumulative", "Comparative"]}
                selected={isComparative ? "Comparative" : "Cumulative"}
                onSelect={(type: string) =>
                  setIsComparative(type === "Comparative")
                }
              />
            )}
          </div>
          {isByChart && (
            <FilterTab
              options={["Daily", "Monthly"]}
              isRounded={true}
              selected={isByMonthlyChart ? ["Monthly"] : ["Daily"]}
              isSecondary={true}
              onClick={(type: string) =>
                setIsByMonthlyChart(type === "Monthly")
              }
            />
          )}
        </div>

        <div className="gas-chart-container mt-5">
          {" "}
          <div className="detContainermain container-radius ">
            <div className="gas-heading gap-x-10 bg-white border-b pb-3 rounded-t-lg ">
              <h2 className="chartHeading w-60">Reverse Auction</h2>
              <div className="flex gap-x-6  justify-end">
                {" "}
                <div className="search-box">
                  <SearchBox
                    options={filteredAuctions(false)
                      .map((auction) => ({
                        label: auction.buyer__name!,
                        value: auction.buyer__name!,
                      }))
                      .filter(
                        (v, i, a) =>
                          a.findIndex((t) => t.value === v.value) === i
                      )}
                    isMany={true}
                    selectedOptions={selectedEntity.map((entity) => ({
                      label: entity,
                      value: entity,
                    }))}
                    onChange={(e: any) => {
                      setSelectedEntity(e.map((item: any) => item.value));
                      setCurrentPage(1);
                    }}
                    placeholder="Select Entity"
                  />
                </div>
                <div className="search-box">
                  <SearchBox
                    options={filteredAuctions(true, true, false)
                      .map((auction) => ({
                        label: formatDMY(auction.auction_date!),
                        value: auction.auction_date!,
                      }))
                      .filter(
                        (v, i, a) =>
                          a.findIndex((t) => t.value === v.value) === i
                      )
                      .sort(
                        (a, b) =>
                          new Date(b.value).getTime() -
                          new Date(a.value).getTime()
                      )}
                    isMany={true}
                    selectedOptions={selectedAuctionData.map((auctionData) => ({
                      label: auctionData,
                      value: auctionData,
                    }))}
                    onChange={(e: any) =>
                      setSelectedAuctionData(e.map((item: any) => item.value))
                    }
                    placeholder="Auction Date"
                  />
                </div>
                <div className="auctionnum-search-box">
                  <SearchBox
                    options={filteredAuctions(true, false, true)
                      .map((auction) => ({
                        label: auction.auction_no!,
                        value: auction.auction_no!,
                      }))
                      .filter(
                        (v, i, a) =>
                          a.findIndex((t) => t.value === v.value) === i
                      )}
                    isMany={true}
                    selectedOptions={selectedAuctioNumber.map(
                      (auctionNumber) => ({
                        label: auctionNumber,
                        value: auctionNumber,
                      })
                    )}
                    onChange={(e: any) => {
                      setSelectedAuctioNumber(e.map((item: any) => item.value));
                      setCurrentPage(1);
                    }}
                    placeholder="Select Auction No."
                  />
                </div>
                <button
                  onClick={() => {
                    HandleDownload(sortedAuctions);
                  }}
                  className="downloadButton"
                >
                  Download
                </button>
              </div>
            </div>
            {isByChart ? (
              <div className="raChart rounded-b-lg relative">
                <div className="flex justify-center absolute top-1 left-0 right-0 z-10">
                  {isComparative && (
                    <div className="flex gap-x-8">
                      {LegendComponent(
                        {
                          dataKey: "total_mu",
                          stroke: COLOR_CODES.secondary,
                          name: "Volume(MUs)",
                          type: 1,
                          legendColor: COLOR_CODES.secondary,
                        },
                        0,
                        [],
                        () => {}
                      )}
                      {LegendComponent(
                        {
                          dataKey: "weighted_avg_rs_per_kwh",
                          stroke: COLOR_CODES.secondary,
                          name: "Wt. Avg Price(Rs/KWh)",
                          yAxisId: "right",
                        },
                        1,
                        [],
                        () => {}
                      )}
                    </div>
                  )}
                </div>
                {!isComparative ? (
                  <>
                    <ReMixChart
                      onlyTitle={true}
                      data={
                        isByMonthlyChart
                          ? FormatAuctionMonthly({
                              start_date: formatDateString(startDate),
                              end_date: formatDateString(endDate),
                              auctions: filteredAuctions(),
                            })
                          : (FormatAuctionDaily({
                              auctions: filteredAuctions(),
                              start_date: startDate.toString(),
                              end_date: endDate.toString(),
                            }) as any)
                      }
                      xDataKey="date"
                      legends={[
                        {
                          dataKey: "total_mu",
                          stroke: "#E0E0E0",
                          name: "Allocated MU",
                          type: 1,
                          stackId: "a",
                          legendColor: "rgb(159, 159, 159)",
                        },
                        {
                          dataKey: "unallocated_mu",
                          stroke: "gray",
                          name: "Unallocated MU",
                          type: 1,
                          stackId: "a",
                          legendColor: "gray",
                        },
                        {
                          dataKey: "weighted_avg_rs_per_kwh",
                          stroke: PrimaryColor,
                          name: "Wt. Avg Price(Rs/KWh)",
                          yAxisId: "right",
                        },
                      ]}
                      xaxisHeight={50}
                      unit="MUs"
                      secondYAxisLabel="Weighted Average Price(Rs/KWh)"
                      xLabel="Date"
                      yAxisLabel="Total MUs"
                    />
                  </>
                ) : (
                  <ReMixChart
                    onlyTitle={true}
                    data={
                      isByMonthlyChart
                        ? FormatAuctionMonthlyComparison({
                            start_date: formatDateString(startDate),
                            end_date: formatDateString(endDate),
                            auctions: filteredAuctions(),
                          })
                        : (FormatAuctionDailyComparison({
                            auctions: filteredAuctions(),
                            start_date: startDate.toString(),
                            end_date: endDate.toString(),
                          }) as any) ?? []
                    }
                    xDataKey="date"
                    legendBreakIndex={
                      getActiveExc().length * 2 - 1 < 2
                        ? undefined
                        : getActiveExc().length * 2 - 1
                    }
                    xaxisHeight={50}
                    legends={[
                      ...getActiveExc()
                        .map((type, index) =>
                          [
                            {
                              dataKey: `total_mu_${type.toLowerCase()}`,
                              stroke: lightenColor(
                                EXCHANGE_COLOR_MAP[type],
                                40
                              ),
                              name: `${type} Allocated`,
                              stackId: type,
                              type: 1,
                            },
                            {
                              dataKey: `unallocated_mu_${type.toLowerCase()}`,
                              stroke: lightenColor(
                                EXCHANGE_COLOR_MAP[type],
                                10
                              ),
                              name: `${type} Unallocated`,
                              stackId: type,
                              type: 1,
                            },
                          ].flat()
                        )
                        .flat(),
                      ...getActiveExc()
                        .map((type, index) =>
                          [
                            {
                              dataKey: `weighted_avg_rs_per_kwh_${type.toLowerCase()}`,
                              stroke: EXCHANGE_COLOR_MAP[type],
                              name: `${type} Price`,
                              yAxisId: "right",
                            },
                          ].flat()
                        )
                        .flat(),
                    ]}
                    brushIndex={{
                      startIndex: 0,
                      endIndex:
                        (
                          FormatAuctionDailyComparison({
                            auctions: filteredAuctions(),
                            start_date: startDate.toString(),
                            end_date: endDate.toString(),
                          }) as any
                        ).length - 1,
                    }}
                    unit="MUs"
                    secondYAxisLabel="Weighted Average Price(Rs/KWh)"
                    xLabel="Date"
                    yAxisLabel="Total MUs"
                    showBrush={
                      !isByMonthlyChart &&
                      (
                        FormatAuctionDailyComparison({
                          auctions: filteredAuctions(),
                          start_date: startDate.toString(),
                          end_date: endDate.toString(),
                        }) as any
                      ).length > 10
                    }
                  />
                )}
              </div>
            ) : (
              <AuctionTable auctions={filteredAuctions()} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const EXCHANGE_TYPES = ["IEX", "HPX", "PXIL", "DEEP"];

const formatDateString = (date: Date): string =>
  date.toLocaleDateString("en-GB").split("/").reverse().join("-");
